import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { Plus, X, Sparkles, Landmark, TrendingUp, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

const TARGETS = [
  { id: "government", label: "Government", icon: Landmark },
  { id: "investor", label: "Investor", icon: TrendingUp },
];

export default function Engagement() {
  const [target, setTarget] = useState("government");
  const [briefings, setBriefings] = useState([]);
  const [stakeholders, setStakeholders] = useState([]);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    const r = await api.get(`/briefings?target=${target}`);
    setBriefings(r.data);
    if (r.data[0]) setActive(r.data[0]);
    else setActive(null);
  };

  useEffect(() => {
    refresh();
    api.get(`/stakeholders?category=${target}`).then((r) => setStakeholders(r.data));
  }, [target]);

  const generate = async () => {
    if (!form.title || !form.objective) {
      toast.error("Title and objective required");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/briefings/generate", { ...form, target });
      setActive(r.data);
      setOpen(false); setForm({});
      refresh();
      toast.success("Briefing synthesized");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove briefing?")) return;
    await api.delete(`/briefings/${id}`);
    refresh();
  };

  return (
    <div className="p-8 space-y-6" data-testid="engagement-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2">// Engagement Hub</div>
          <h1 className="font-display text-4xl font-medium text-white tracking-tight">
            Government & Investor Engagement
          </h1>
          <p className="text-slate-400 mt-2 text-sm">AI-generated briefing notes, strategic positioning, and protocol-aware preparation.</p>
        </div>
        <button
          onClick={() => { setForm({ target }); setOpen(true); }}
          data-testid="new-briefing-btn"
          className="px-4 py-2 flex items-center gap-2 font-medium text-sm"
          style={{ background: "var(--gold-500)", color: "#050B14" }}
        >
          <Sparkles className="w-4 h-4" /> Generate Briefing
        </button>
      </div>

      <div className="flex border border-white/10 w-fit">
        {TARGETS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTarget(t.id)}
            data-testid={`tab-${t.id}`}
            className={`px-6 py-2.5 flex items-center gap-2 text-sm font-mono tracking-widest ${target === t.id ? "bg-[#D4AF37] text-[#050B14]" : "text-slate-400 hover:text-white"}`}
          >
            <t.icon className="w-4 h-4" strokeWidth={1.5} /> {t.label.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-4 space-y-2">
          <div className="label-eyebrow mb-2">// Briefings ({briefings.length})</div>
          {briefings.map((b) => (
            <motion.div
              layout key={b.id}
              onClick={() => setActive(b)}
              data-testid={`briefing-${b.id}`}
              className={`p-4 cursor-pointer transition-all border ${active?.id === b.id ? "border-[#D4AF37]/50 bg-[#10182B]" : "border-white/10 hover:border-[#D4AF37]/30 bg-[#0A111F]"}`}
            >
              <div className="label-eyebrow mb-2">{b.target}</div>
              <div className="font-display text-base text-white leading-tight">{b.title}</div>
              <div className="text-xs text-slate-500 mt-2">{b.objective?.slice(0, 80)}</div>
              <div className="font-mono text-[10px] text-slate-600 mt-2">
                {new Date(b.created_at).toLocaleDateString()}
              </div>
            </motion.div>
          ))}
          {!briefings.length && (
            <div className="text-sm text-slate-500 p-6 border border-white/5">
              No briefings yet for {target}. Generate your first one.
            </div>
          )}

          <div className="mt-6">
            <div className="label-eyebrow mb-2">// Mapped Stakeholders ({stakeholders.length})</div>
            <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
              {stakeholders.map((s) => (
                <div key={s.id} className="p-2.5 border border-white/5 hover:border-white/15 text-xs">
                  <div className="text-white truncate">{s.name}</div>
                  <div className="text-slate-500 truncate font-mono text-[10px] mt-0.5">{s.organization}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-8">
          {active ? (
            <div className="nexus-card">
              <div className="px-8 py-5 border-b border-white/10 flex items-start justify-between">
                <div>
                  <div className="label-eyebrow mb-1">// {active.target} engagement</div>
                  <h2 className="font-display text-2xl text-white">{active.title}</h2>
                  {active.objective && <div className="text-sm text-slate-400 mt-2 max-w-2xl">{active.objective}</div>}
                </div>
                <button onClick={() => remove(active.id)} className="p-2 text-slate-500 hover:text-red-400 border border-white/10 hover:border-red-500/50">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="p-8 max-h-[65vh] overflow-y-auto">
                <div className="nexus-prose whitespace-pre-wrap">{active.content}</div>
              </div>
            </div>
          ) : (
            <div className="nexus-card p-10 text-center text-slate-500 text-sm">
              No briefing selected. Generate one above.
            </div>
          )}
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setOpen(false)}>
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="bg-[#0A111F] border border-white/10 p-8 w-full max-w-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="briefing-form"
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="label-eyebrow mb-1">// AI Briefing</div>
                <h2 className="font-display text-2xl text-white">Generate {target} briefing</h2>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="label-eyebrow block mb-1.5">Title *</label>
                <input
                  data-testid="brief-title"
                  value={form.title || ""}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full px-3 py-2 bg-[#050B14] border border-white/10 text-white font-mono text-sm focus:border-[#D4AF37] outline-none"
                  placeholder="e.g., Ministry of Agriculture — PPP brief"
                />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Objective *</label>
                <textarea
                  data-testid="brief-objective"
                  rows={3}
                  value={form.objective || ""}
                  onChange={(e) => setForm({ ...form, objective: e.target.value })}
                  className="w-full px-3 py-2 bg-[#050B14] border border-white/10 text-white font-mono text-sm focus:border-[#D4AF37] outline-none resize-none"
                  placeholder="What outcome are we seeking?"
                />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Anchor on stakeholder (optional)</label>
                <select
                  value={form.stakeholder_id || ""}
                  onChange={(e) => setForm({ ...form, stakeholder_id: e.target.value })}
                  className="w-full px-3 py-2 bg-[#050B14] border border-white/10 text-white font-mono text-sm focus:border-[#D4AF37] outline-none"
                >
                  <option value="">— None —</option>
                  {stakeholders.map((s) => <option key={s.id} value={s.id}>{s.name} — {s.organization}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setOpen(false)} className="px-5 py-2 border border-white/10 text-slate-300">Cancel</button>
              <button
                onClick={generate}
                disabled={busy}
                data-testid="brief-submit"
                className="px-5 py-2 font-medium flex items-center gap-2 disabled:opacity-60"
                style={{ background: "var(--gold-500)", color: "#050B14" }}
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {busy ? "Synthesizing…" : "Generate"}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
